import { useState } from 'react'
import { lumi } from '@/lib/lumi'
import { notifyNotificationsChanged } from '@/hooks/useUnreadNotifications'

export interface AppNotification {
  _id: string
  title: string
  body: string
  type: 'vacante' | 'curso' | 'evento' | 'aviso' | 'recordatorio'
  targetAudience: 'empresas' | 'colaboradores' | 'todos'
  targetUserId?: string
  status: 'borrador' | 'publicada' | 'archivada'
  readBy: string[]
  linkUrl?: string
  creator: string
  createdAt: string
  updatedAt: string
}

export const NOTIF_TYPE_LABELS: Record<AppNotification['type'], string> = {
  vacante: 'Vacante',
  curso: 'Curso',
  evento: 'Evento',
  aviso: 'Aviso',
  recordatorio: 'Recordatorio',
}

export const NOTIF_TYPE_COLORS: Record<AppNotification['type'], string> = {
  vacante: 'bg-blue-100 text-blue-700',
  curso: 'bg-violet-100 text-violet-700',
  evento: 'bg-emerald-100 text-emerald-700',
  aviso: 'bg-amber-100 text-amber-700',
  recordatorio: 'bg-orange-100 text-orange-700',
}

export const AUDIENCE_LABELS: Record<AppNotification['targetAudience'], string> = {
  empresas: 'Empresas',
  colaboradores: 'Colaboradores',
  todos: 'Todos',
}

export function useNotifications() {
  const [notifications, setNotifications] = useState<AppNotification[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /** Carga notificaciones publicadas para una audiencia específica */
  const fetchForAudience = async (audience: 'empresas' | 'colaboradores', userId?: string) => {
    setLoading(true)
    setError(null)
    try {
      const res = await lumi.entities.notifications.list({
        filter: {
          status: 'publicada',
          $or: [
            { targetAudience: audience },
            { targetAudience: 'todos' },
            ...(userId ? [{ targetUserId: userId }] : []),
          ],
        },
        sort: { createdAt: -1 },
        limit: 100,
      })
      setNotifications(res.list as AppNotification[])
    } catch (e) {
      console.error('[useNotifications] fetchForAudience error:', e)
      setError('No se pudieron cargar las notificaciones.')
    } finally {
      setLoading(false)
    }
  }

  /** Admin: carga todas las notificaciones */
  const fetchAll = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await lumi.entities.notifications.list({
        sort: { createdAt: -1 },
        limit: 500,
      })
      setNotifications(res.list as AppNotification[])
    } catch (e) {
      console.error('[useNotifications] fetchAll error:', e)
      setError('No se pudieron cargar los comunicados.')
    } finally {
      setLoading(false)
    }
  }

  const createNotification = async (data: Omit<AppNotification, '_id'>) => {
    const now = new Date().toISOString()
    const doc = { ...data, createdAt: now, updatedAt: now }
    const res = await lumi.entities.notifications.create(doc)
    return res as AppNotification
  }

  const updateNotification = async (id: string, data: Partial<AppNotification>) => {
    const now = new Date().toISOString()
    await lumi.entities.notifications.update(id, { ...data, updatedAt: now })
  }

  /**
   * Envía una notificación dirigida a un usuario específico (colaborador o empresa).
   * Se usa para eventos automáticos: vinculación aceptada, caso/reporte abierto, desvinculación, etc.
   */
  const notifyUser = async (params: {
    targetUserId: string
    title: string
    body: string
    type?: AppNotification['type']
    audience?: AppNotification['targetAudience']
    linkUrl?: string
    creator?: string
  }) => {
    const now = new Date().toISOString()
    try {
      await lumi.entities.notifications.create({
        title: params.title,
        body: params.body,
        type: params.type || 'aviso',
        targetAudience: params.audience || 'colaboradores',
        targetUserId: params.targetUserId,
        status: 'publicada',
        readBy: [],
        linkUrl: params.linkUrl || '',
        creator: params.creator || params.targetUserId,
        createdAt: now,
        updatedAt: now,
      })
    } catch (e) {
      console.error('[useNotifications] notifyUser error:', e)
    }
  }

  /** Marca una notificación como leída por un userId */
  const markRead = async (notif: AppNotification, userId: string) => {
    if (notif.readBy?.includes(userId)) return
    const readBy = [...(notif.readBy ?? []), userId]
    await lumi.entities.notifications.update(notif._id, {
      readBy,
      updatedAt: new Date().toISOString(),
    })
    setNotifications((prev) =>
      prev.map((n) => (n._id === notif._id ? { ...n, readBy } : n))
    )
    notifyNotificationsChanged()
  }

  /** Marca todas como leídas */
  const markAllRead = async (userId: string) => {
    const unread = notifications.filter((n) => !(n.readBy ?? []).includes(userId))
    await Promise.all(unread.map((n) => markRead(n, userId)))
    notifyNotificationsChanged()
  }

  return {
    notifications,
    loading,
    error,
    fetchForAudience,
    fetchAll,
    createNotification,
    updateNotification,
    notifyUser,
    markRead,
    markAllRead,
  }
}
